import { useEffect, useState } from "react";
import { ArrowLeft, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../../components/Layout";
import api, { downloadFile, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

export default function Export() {
  const [comps, setComps] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/competitions")
      .then((r) => setComps(r.data.filter((c) => c.sent)))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const onDownload = async (fmt) => {
    if (!selectedId) return;
    setDownloading(fmt);
    try {
      await downloadFile(`/competitions/${selectedId}/results.${fmt}`, `risultati.${fmt}`);
      toast.success(`Download ${fmt.toUpperCase()} avviato`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(null);
    }
  };

  const selected = comps.find((c) => c.id === selectedId);

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="mb-8 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Tools Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Export Risultati</h1>
          <p className="text-slate-500 mt-1">Scarica i risultati di una competizione in CSV o Excel.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8">
        <label className="text-sm font-semibold text-slate-700">Seleziona competizione</label>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="mt-2 h-11 max-w-2xl" data-testid="export-select">
            <SelectValue placeholder={comps.length ? "Scegli una competizione…" : "Nessuna competizione inviata"} />
          </SelectTrigger>
          <SelectContent>
            {comps.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`export-option-${c.id}`}>
                {c.name} — {c.location} — {new Date(c.date).toLocaleDateString("it-IT")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selected && (
          <div className="mt-6 p-5 rounded-xl bg-slate-50 border border-slate-200">
            <div className="font-display font-semibold text-slate-900">{selected.name}</div>
            <div className="text-sm text-slate-600 mt-1">
              {selected.location} · {new Date(selected.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })} · {selected.athletes?.length || 0} atleti
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Button
                onClick={() => onDownload("csv")}
                disabled={!!downloading}
                className="bg-blue-700 hover:bg-blue-800 text-white h-12 px-6"
                data-testid="export-csv-button"
              >
                {downloading === "csv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Scarica CSV
              </Button>
              <Button
                onClick={() => onDownload("xlsx")}
                disabled={!!downloading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6"
                data-testid="export-xlsx-button"
              >
                {downloading === "xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Scarica Excel
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Il file include numero gara, atleta, voti per giudice, medie, totale generale e posizione in classifica.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
